/**
 * @fileoverview ButtonWithConfirmation Component
 * A reusable confirmation dialog component that wraps either an IconButton or regular Button
 * with a tooltip-based confirmation dialog.
 * 
 * @author SmarterServices Platform Team
 * @version 2.0.0
 * @since 2025-11-21
 */

import React, { useState, useRef } from 'react';
import { IconButton, Tooltip, View, Text, Flex, Button } from '@smarterservices/smarter-ui';
import { useFullScreenMountNode } from '../../proctoring/SessionViewer/contexts/FullScreenContext';

/**
 * Props for ButtonWithConfirmation component
 */
interface ButtonWithConfirmationProps {
  // Main button props
  onConfirm: () => void;
  onCancel?: () => void;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'success' | 'danger' | 'primary-inverse';
  margin?: string;
  screenReaderLabel?: string;
  disabled?: boolean;
  variant?: 'icon' | 'button'; // Controls whether to use IconButton or Button
  
  // Confirmation dialog props
  confirmationText?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: 'primary' | 'secondary' | 'success' | 'danger' | 'primary-inverse';
  
  // Tooltip props (allow overrides)
  tooltipProps?: {
    placement?: 'top' | 'bottom' | 'start' | 'end';
    color?: 'primary' | 'primary-inverse';
    offsetX?: number;
    offsetY?: number;
    on?: ('click' | 'hover' | 'focus')[];
  };
  
  // View props for confirmation dialog
  viewProps?: {
    padding?: string;
    maxWidth?: string;
    textAlign?: 'start' | 'center' | 'end';
  };
  
  // Text props for confirmation message
  textProps?: {
    size?: 'x-small' | 'small' | 'medium' | 'large' | 'x-large';
    as?: 'div' | 'span' | 'p';
  };
  
  // Flex props for button container
  flexProps?: {
    direction?: 'row' | 'column';
    justifyItems?: 'start' | 'center' | 'end' | 'space-around' | 'space-between';
  };
  
  // Individual button styling
  confirmButtonProps?: {
    size?: 'small' | 'medium' | 'large';
    margin?: string;
    padding?: string;
  };
  
  cancelButtonProps?: {
    size?: 'small' | 'medium' | 'large';
    margin?: string;
    padding?: string;
  };
}

/**
 * ButtonWithConfirmation Component
 * 
 * Displays either an IconButton or regular Button that shows a confirmation dialog
 * when clicked. The dialog remains open until the user confirms or cancels.
 * 
 * @example
 * // Icon button with confirmation (default)
 * <ButtonWithConfirmation onConfirm={handleDelete}>
 *   <IconTrashLine />
 * </ButtonWithConfirmation>
 * 
 * @example
 * // Regular button with confirmation
 * <ButtonWithConfirmation
 *   variant="button"
 *   onConfirm={handleSubmit}
 *   confirmationText="Are you sure you want to submit?"
 * >
 *   Submit
 * </ButtonWithConfirmation>
 * 
 * @param props - Component props
 * @returns React component
 */
export function ButtonWithConfirmation({
  onConfirm,
  onCancel,
  children,
  size = 'small',
  color = 'primary',
  margin = 'none x-small none none',
  screenReaderLabel = 'Action Button',
  disabled = false,
  variant = 'icon',
  confirmationText = 'Are you sure?',
  confirmButtonText = 'Yes',
  cancelButtonText = 'No',
  confirmButtonColor = 'primary',
  tooltipProps = {},
  viewProps = {},
  textProps = {},
  flexProps = {},
  confirmButtonProps = {},
  cancelButtonProps = {},
}: ButtonWithConfirmationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipTriggerRef = useRef<HTMLElement>(null);
  
  // Get the appropriate mount node for full screen support
  const mountNode = useFullScreenMountNode();

  const handleConfirm = () => {
    setIsOpen(false);
    onConfirm();
  };

  const handleCancel = () => {
    setIsOpen(false);
    onCancel?.();
  };

  // Merge default tooltip props with overrides
  const mergedTooltipProps = {
    placement: 'bottom' as const,
    color: 'primary' as const,
    offsetY: 5,
    on: ['click'], // Only click, not hover - prevents closing on mouse leave
    ...tooltipProps,
  };

  // Merge default view props with overrides
  const mergedViewProps = {
    padding: 'xx-small',
    maxWidth: '200px',
    textAlign: 'center' as const,
    ...viewProps,
  };

  // Merge default text props with overrides
  const mergedTextProps = {
    size: 'small' as const,
    as: 'div' as const,
    ...textProps,
  };

  // Merge default flex props with overrides
  const mergedFlexProps = {
    direction: 'row' as const,
    justifyItems: 'center' as const,
    ...flexProps,
  };

  // Merge default button props with overrides
  const mergedConfirmButtonProps = {
    size: 'small' as const,
    padding: 'xx-small',
    ...confirmButtonProps,
  };

  const mergedCancelButtonProps = {
    size: 'small' as const,
    padding: 'xx-small',
    ...cancelButtonProps,
  };

  return (
    <Tooltip
      renderTip={
        <View
          as="div"
          padding={mergedViewProps.padding}
          maxWidth={mergedViewProps.maxWidth}
          textAlign={mergedViewProps.textAlign}
        >
          <Text 
            as={mergedTextProps.as} 
            size={mergedTextProps.size}
          >
            {confirmationText}
          </Text>
          <Flex 
            direction={mergedFlexProps.direction}
            justifyItems={mergedFlexProps.justifyItems}
          >
            <Flex.Item padding={mergedConfirmButtonProps.padding}>
              <Button
                size={mergedConfirmButtonProps.size}
                color={confirmButtonColor}
                onClick={handleConfirm}
                margin={mergedConfirmButtonProps.margin}
              >
                {confirmButtonText}
              </Button>
            </Flex.Item>
            <Flex.Item padding={mergedCancelButtonProps.padding}>
              <Button
                size={mergedCancelButtonProps.size}
                onClick={handleCancel}
                margin={mergedCancelButtonProps.margin}
              >
                {cancelButtonText}
              </Button>
            </Flex.Item>
          </Flex>
        </View>
      }
      on={mergedTooltipProps.on as any}
      placement={mergedTooltipProps.placement}
      color={mergedTooltipProps.color}
      offsetX={mergedTooltipProps.offsetX}
      offsetY={mergedTooltipProps.offsetY}
      mountNode={() => mountNode}
      isShowingContent={isOpen}
      onShowContent={() => setIsOpen(true)}
      onHideContent={(event: any) => {
        // Only close on click, not on mouse leave
        if (event?.type === 'click') {
          setIsOpen(false);
        }
      }}
    >
      {variant === 'icon' ? (
        <IconButton
          size={size}
          color={color}
          margin={margin}
          screenReaderLabel={screenReaderLabel}
          disabled={disabled}
          elementRef={(el) => {
            if (el) {
              (tooltipTriggerRef as any).current = el;
            }
          }}
        >
          {children}
        </IconButton>
      ) : (
        <span>
          <Button
            size={size}
            color={color}
            margin={margin}
            disabled={disabled}
            elementRef={(el) => {
              if (el) {
                (tooltipTriggerRef as any).current = el;
              }
            }}
          >
            {children}
          </Button>
        </span>
      )}
    </Tooltip>
  );
}

export default ButtonWithConfirmation;